import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { contactApi } from '../services/authApi';
import Select from './Select';
import ErrorDisplay from './ui/ErrorDisplay';

interface ContactModalProps {
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'question', label: 'Вопрос' },
  { value: 'problem', label: 'Проблема' },
  { value: 'suggestion', label: 'Предложение' },
  { value: 'other', label: 'Другое' },
];

const ContactModal: React.FC<ContactModalProps> = ({ onClose }) => {
  const { user } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [category, setCategory] = useState('question');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await contactApi.send({ name, email, category, message });
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Не удалось отправить сообщение');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-6 pb-8 shadow-xl animate-slide-up">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {sent ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-50 mx-auto mb-3 flex items-center justify-center">
              <svg className="w-7 h-7 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Сообщение отправлено</h2>
            <p className="text-sm text-gray-500">Мы ответим вам на {email}</p>
            <button
              onClick={onClose}
              className="w-full mt-6 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition-all"
            >
              Закрыть
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">Написать в поддержку</h2>
              <p className="text-sm text-gray-500 mt-0.5">Ответим на вашу почту</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Имя</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ваше имя"
                  required
                  maxLength={100}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email для ответа</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  maxLength={255}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Тема</label>
                <Select
                  value={category}
                  options={CATEGORIES}
                  onChange={setCategory}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Сообщение</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Опишите ваш вопрос или проблему..."
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all resize-none"
                />
              </div>
            </div>

            <ErrorDisplay error={error} />

            <button
              type="submit"
              disabled={loading || !name || !email || !message}
              className="w-full mt-4 py-3 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? 'Отправка...' : 'Отправить'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
